/**
 * Mobile drawer context — the open/close handle for <Shell>'s off-canvas
 * left drawer, shared with whatever renders the burger.
 *
 * It lives in its own module because <Shell> now mounts <ScopeBar> itself
 * (2026-08-01, QA D-1/D-2/D-3) while <ScopeBar> needs this hook: keeping the
 * context inside shell.tsx would make shell ⇄ scope-bar a genuine import
 * cycle. <Shell> re-exports `useMobileDrawer` so existing imports keep working.
 */
import * as React from 'react';

export interface MobileDrawerCtx {
  open: boolean;
  setOpen: (v: boolean) => void;
}

export const MobileDrawerContext = React.createContext<MobileDrawerCtx | null>(null);

/** Hook for descendants (e.g. the ScopeBar burger) to control the drawer. */
export function useMobileDrawer(): MobileDrawerCtx {
  const ctx = React.useContext(MobileDrawerContext);
  if (!ctx) {
    // Soft-fail off the shell (e.g. login page) — return a no-op so callers
    // don't crash.
    return { open: false, setOpen: () => undefined };
  }
  return ctx;
}
